import type { StatsResponse, Timeseries } from '~/api/types'
import { Skeleton } from '~/components/ui/skeleton'
import {
    ChartCard,
    DonutChart,
    CategoryBarChart,
    Gauge,
    TrendLines,
    TrendBars,
    KRDS,
    statusColor,
    type DonutDatum,
    type CategoryDatum,
} from '~/components/charts'
import {
    statusLabel,
    formatPeriodLabel,
    type View,
} from '~/components/dashboard/constants'

// 3) 차트 영역 — 통계 ↔ 시계열 토글.
export function ChartSection({
    loading,
    view,
    stats,
    timeseries,
    days,
}: {
    loading: boolean
    view: View
    stats: StatsResponse | null
    timeseries: Timeseries | null
    days: number
}) {
    // 인증평가 상태 분포 (도넛)
    const cert = stats?.certification
    const certDist: DonutDatum[] = cert
        ? [
              {
                  name: statusLabel('collecting'),
                  value: cert.collecting,
                  color: statusColor('collecting'),
              },
              {
                  name: statusLabel('scored'),
                  value: cert.scored,
                  color: statusColor('scored'),
              },
              {
                  name: statusLabel('reported'),
                  value: cert.reported,
                  color: statusColor('reported'),
              },
          ]
        : []
    const certEmpty = !cert || cert.total === 0

    // 문서 파이프라인 (가로 바)
    const docEntries = Object.entries(stats?.documents.byStatus ?? {})
    const docTotal = docEntries.reduce((s, [, v]) => s + v, 0)
    const docData: CategoryDatum[] = docEntries.map(([status, value]) => ({
        name: statusLabel(status),
        value,
        color: statusColor(status),
    }))
    const docEmpty = !stats || docTotal === 0

    // 서비스별 활동량 (세로 바)
    const svcData: CategoryDatum[] = stats
        ? [
              {
                  name: '상담',
                  value: stats.consultation.sessions,
                  color: KRDS.primary,
              },
              {
                  name: '인증평가',
                  value: stats.certification.total,
                  color: KRDS.info,
              },
              {
                  name: '유지하기',
                  value: stats.maintenance.events,
                  color: KRDS.secondary,
              },
              {
                  name: '모니터링',
                  value: stats.monitoring.dashboards,
                  color: KRDS.success,
              },
          ]
        : []
    const svcEmpty = !stats || svcData.every((d) => d.value === 0)

    // 만족도
    const sat = stats?.satisfaction
    const satRate = sat && sat.total > 0 ? sat.upRate : 0
    const satEmpty = !sat || sat.total === 0

    // 시계열
    const periodLabel = formatPeriodLabel(days)
    const tsEmpty = !timeseries || timeseries.totalEvents === 0

    return loading ? (
        <div className='mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2'>
            <Skeleton className='h-72' />
            <Skeleton className='h-72' />
        </div>
    ) : view === 'stats' ? (
        <>
            {/* 분포·파이프라인 */}
            <div className='mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2'>
                <ChartCard
                    title='인증평가 상태 분포'
                    description='수집 → 채점 → 보고서 발행 단계별 건수'
                    empty={certEmpty}
                    emptyText='인증평가 데이터가 없어요.'>
                    <DonutChart
                        data={certDist}
                        centerValue={cert?.total ?? 0}
                        centerLabel='전체 평가'
                    />
                </ChartCard>

                <ChartCard
                    title='문서 파이프라인'
                    description={`상태별 문서 처리 현황 · 전체 ${docTotal}건`}
                    empty={docEmpty}
                    emptyText='문서가 없어요.'>
                    <CategoryBarChart data={docData} layout='vertical' />
                </ChartCard>
            </div>

            {/* 서비스 활동량·만족도 */}
            <div className='mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3'>
                <ChartCard
                    title='서비스별 활동량'
                    description='상담 세션 · 인증평가 · 유지 이벤트 · 모니터링'
                    className='lg:col-span-2'
                    empty={svcEmpty}
                    emptyText='활동 데이터가 없어요.'>
                    <CategoryBarChart data={svcData} layout='horizontal' />
                </ChartCard>

                <ChartCard
                    title='사용자 만족도'
                    description='긍정 피드백 비율'
                    empty={satEmpty}
                    emptyText='피드백이 없어요.'>
                    <Gauge ratio={satRate} label='👍 긍정 비율' />
                    <div className='mt-2 flex justify-center gap-6 text-sm'>
                        <span className='text-success font-bold'>
                            👍 {sat?.up ?? 0}
                        </span>
                        <span className='text-point font-bold'>
                            👎 {sat?.down ?? 0}
                        </span>
                    </div>
                </ChartCard>
            </div>
        </>
    ) : (
        <>
            {/* 일별 활동 추이(멀티 시리즈) */}
            <div className='mt-6'>
                <ChartCard
                    title='일별 활동 추이'
                    description={`상담 · 인증 · 유지 · 피드백 · 모니터링 · 최근 ${periodLabel}`}
                    empty={tsEmpty}
                    emptyText='기간 내 활동 데이터가 없어요.'>
                    <TrendLines data={timeseries?.series ?? []} />
                </ChartCard>
            </div>

            {/* 일별 총 활동 히스토그램(트래픽) */}
            <div className='mt-4'>
                <ChartCard
                    title='일별 총 활동(트래픽)'
                    description={`일별 활동 이벤트 합계 · 기간 총 ${timeseries?.totalEvents ?? 0}건`}
                    empty={tsEmpty}
                    emptyText='기간 내 활동 데이터가 없어요.'>
                    <TrendBars data={timeseries?.series ?? []} />
                </ChartCard>
            </div>
        </>
    )
}
